<template>
    <div v-if="booted">
        <!-- Si la limite a été atteinte -->
		<RegularBuyPlan v-if="!has_right && !eligible_for_pack" @purchased="hideRegularPurchase" />

		<!-- Bouton pour demander un pack de 1000 chevaux (pour licences Pro) -->
		<div v-else-if="!has_right && eligible_for_pack" class="col-12 text-center mt-3">
			<h2>{{ $t('plan.limite_chevaux_atteinte') }}</h2>
			<p>{{ $t('plan.besoin_plus_chevaux') }}</p>
			<b-button variant="danger" size="lg" @click="showHorsePackModal"> {{ $t('plan.add_horse_pack_button') }}</b-button>
		</div>

        <!-- Formulaire de création -->
        <div v-else class="container-fluid">
        <div class="row">
            <div class="col-12">
                <form @submit.prevent="validForm">
                    <!-- Nom -->
                    <div class="form-group">
                        <label>{{ $t('horse.form.nom') }}<sup>*</sup></label>

                        <div class="input-group">
                            <input class="form-control" type="text" :placeholder="$t('horse.form.nom')" v-model="horse_nom" id="horse_nom" required>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-4">
                            <!-- Sexe -->
                            <div class="form-group">
                                <label>{{ $t('horse.form.sexe') }}<sup>*</sup></label>
                                <e-select
                                    v-model="horse_sexe"
                                    id="sexe_code"
                                    track-by="sexe_code"
                                    label="sexe_label"
                                    :placeholder="$t('horse.placeholder.sexe')"
                                    :selectedLabel="$t('select.selectedLabel')"
                                    :options="horse_sexes"
                                    :searchable="true"
                                    :allow-empty="false"
                                    :loading="isLoadingHorseSexe"
                                    :show-labels="false"
                                >
                                    <template slot="first" slot-scope="{ option }">{{ option.label }}</template>
                                    <template slot="noOptions">{{ $t('global.list_empty') }}</template>
                                </e-select>
                            </div>
                        </div>
                        <div class="col-md-4">
                            <!-- Robe -->
                            <div class="form-group">
                                <label>{{ $t('horse.form.robe') }}</label>
                                <e-select
                                    v-model="horse_robe"
                                    id="robe_code"
                                    track-by="robe_code"
                                    label="robe_label"
                                    :placeholder="$t('horse.placeholder.robe')"
                                    :selectedLabel="$t('select.selectedLabel')"
                                    :options="horse_robes"
                                    :searchable="true"
                                    :allow-empty="false"
                                    :loading="isLoadingHorseRobe"
                                    :show-labels="false"
                                >
                                    <template slot="first" slot-scope="{ option }">{{ option.label }}</template>
                                    <template slot="noOptions">{{ $t('global.list_empty') }}</template>
                                </e-select>
                            </div>
                        </div>
                        <div class="col-md-4">
                            <!-- Race -->
                            <div class="form-group">
                                <label>{{ $t('horse.form.race') }}</label>
                                <e-select
                                    v-model="horse_race"
                                    id="race_code"
                                    track-by="race_code"
                                    label="display_label"
                                    :placeholder="$t('horse.placeholder.race')"
                                    :selectedLabel="$t('select.selectedLabel')"
                                    :options="horse_races"
                                    :searchable="true"
                                    :allow-empty="false"
                                    :loading="isLoadingHorseRace"
                                    :show-labels="false"
                                >
                                    <template slot="first" slot-scope="{ option }">{{ option.label }}</template>
                                    <template slot="noOptions">{{ $t('global.list_empty') }}</template>
                                </e-select>
                            </div>
                        </div>

                        <div class="col-md-4">
                            <!-- Date de naissance -->
                            <div class="form-group">
                                <label>{{ $t('horse.form.datenaissance') }}</label>
                                <div class="input-group">
                                    <e-datepicker v-model="horse_datenaissance" />
                                    <div class="input-group-append">
                                        <span class="input-group-text"><font-awesome-icon :icon="['fal', 'calendar-alt']" /></span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="show_transpondeur" class="col-md-4">
                            <!-- Numéro transpondeur -->
                            <div class="form-group">
                                <label>{{ $t('horse.numero_transpondeur') }}</label>
                                <div class="input-group">
                                    <input class="form-control" type="text" :placeholder="$t('horse.numero_transpondeur')" v-model="horse_transpondeur" id="horse_transpondeur" required>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Message d'erreur si besoin -->
                    <ErrorAlert v-if="message_erreur_code !== ''" :messageI18n="message_erreur_code" />

                    <!-- Bouton de validation du formulaire -->
                    <div class="text-center mt-5">
                        <button type="submit" v-on:click="checkForm" class="btn btn-primary rounded-pill">
                            {{ $t('global.ajouter') }} <font-awesome-icon :icon="['fal', 'plus-circle']"/>
                        </button>
                    </div>
                </form>
            </div>
        </div>
        </div>

        <ModalConfirm
            ref="show_horse_pack_modal"
            id="show_horse_pack_modal_c"
            :icon="['fal', 'check']"
            :icon_accept="['fal', 'level-down']"
            :text_title="$t('plan.add_horse_pack_title')"
            :text_question="$t('plan.add_horse_pack_confirmation')"
            :text_button_ok="$t('plan.add_horse_pack_confirm_button')"
            :text_button_cancel="$t('plan.add_horse_pack_cancel_button')"
            :callback_validation="confirmPackAdd"
            :callback_cancel="cancelPackAdd"
            :processing="loading"
        />
    </div>
</template>

<script type="text/javascript">

    import ConfigMixin from '@/mixins/Config';
    import HorseMixin from '@/mixins/Horse';
    import Tools from '@/mixins/Tools';
    import Actions from '@/mixins/Actions';
    import Horse from "@/mixins/Horse.js";

    export default {
        name: "HorseCreation",
        mixins: [ConfigMixin, HorseMixin, Tools, Actions, Horse],
        data () {
            return {
                booted: false,
                horse_races: [],
                horse_robes: [],
                horse_sexes: [],

                horse_nom: null,
                horse_race: null,
                horse_robe: null,
                horse_sexe: null,
                horse_datenaissance: null,
                horse_transpondeur: null,

                required_values: [
                        'horse_nom',
                        'horse_race',
                        'horse_sexe',
                        'horse_robe'
                    ],
				message_erreur_code: '',
				isLoadingHorseSexe: true,
				isLoadingHorseRobe: true,
				isLoadingHorseRace: true,
                show_transpondeur: false,
                has_right: false,
				code_erreur: '',
                eligible_for_pack: false,
                loading: false
            }
        },
        mounted() {
            this.init_component()
        },
        methods: {
            async init_component() {
                // Vérifier les droits EN PREMIER
                this.eligible_for_pack = this.$store.state.userAccess.hasActTypeAccess
                this.has_right = await this.checkCanAdd()

                if(!this.has_right) {
					this.code_erreur = "NR"
				}

                // Charger les données du formulaire seulement si l'utilisateur a le droit
                this.horse_races = await this.getRaces()
                for (const horse of this.horse_races){
                    horse.display_label = this.$t("race." + horse.race_code) +' / '+ horse.race_label
                }
                this.horse_robes = await this.getRobes()
				this.horse_sexes = await this.getSexes()
				
				this.isLoadingHorseSexe = false
				this.isLoadingHorseRobe = false
				this.isLoadingHorseRace = false

				this.horse_robe = this.horse_robes.find(robe => robe.robe_code === 'UKN')
				this.horse_race = this.horse_races.find(race => race.race_code === 'UKN')

                if(this.$route.params.transpondeur) {
                    this.show_transpondeur = true
                    this.horse_transpondeur = this.$route.params.transpondeur
                }
                this.booted = true
            },

            async validForm() {
                let horse = {
                    horse_nom: this.horse_nom,
                    horse_sexe: this.horse_sexe.sexe_code,
                    horse_robe: this.horse_robe.robe_code,
                    horse_race: this.horse_race.race_code,
                    horse_datenaissance: this.horse_datenaissance,
                    horse_transpondeur: this.horse_transpondeur
                }
				let horse_id = await this.createHorseOffline(horse)

                if(horse_id !== 0) {
                    if(this.$route.params.action) {
                        this[this.$route.params.action](horse_id)
                    }
                    else if(this.getConfig('skip_residence_onboarding')) {
                        this.$router.push({ name: 'horseListe' })
                    }
                    else {
                        this.$router.push({ name: 'mouvementAjout', params: { horse_id: horse_id, from: this.$options.name, type: 'entree' }})
                    }
                } else {
                    this.message_erreur_code = "error.LEP"
                }
            },

            checkForm() {
                let el = ''
                let error = false

                // On vérifie chaque input obligatoire
                for(let i=0; i<this.required_values.length; i++) {
                    el = this.required_values[i]

                    // On ajoute la classe error sur les div des v-model non remplis
                    if(this[el] === null || this[el] === undefined) {
                        error = true
                    }
                }

                if(error) {
                    this.message_erreur_code = "formulaire.erreur_champs_non_remplis"
                }
            },

            hideRegularPurchase() {
				this.code_erreur = ''
				this.has_right = true
			},

            showHorsePackModal() {
                this.$refs.show_horse_pack_modal.openModal()
            },

            cancelPackAdd() {
                this.$refs.show_horse_pack_modal.closeModal()
            },

            async confirmPackAdd() {
                this.loading = true
                const result = await this.saveRequestHorsePack()
                if (result) {
                    this.loading = false
                    this.successToast(this.getTrad('plan.add_horse_pack_success'))
                    window.location.reload()
                } else {
                    this.loading = false
                    this.failureToast(this.getTrad('plan.add_horse_pack_error'), true)
                    this.$refs.show_horse_pack_modal.closeModal()
                }
            }
        },
        components: {
            HeaderTab: () => import('@/components/HeaderTab'),
            ErrorAlert: () => import('GroomyRoot/components/Alert/ErrorAlert'),
            RegularBuyPlan: () => import('@/components/Plans/BuyPlan'),
            ModalConfirm: () => import('@/components/Modals/ModalConfirm')
        }
    };
</script>
